// A failed upload, classified so the slot can show a persistent, actionable message.
// The server's own reason (`detail`) is kept — never replaced by a generic string.

export type UploadFailureKind = "rejected" | "network";

export class UploadError extends Error {
  readonly kind: UploadFailureKind;
  /** HTTP status for a rejection; undefined for a network failure. */
  readonly status?: number;
  /** Stable code when the server sent one ({detail: {code, message}}); for a network
   *  failure, "timeout" when the client gave up waiting, otherwise unset. */
  readonly code?: string;

  constructor(kind: UploadFailureKind, message: string, status?: number, code?: string) {
    super(message);
    this.name = "UploadError";
    this.kind = kind;
    this.status = status;
    this.code = code;
  }
}

export const NETWORK_FAILURE_MESSAGE = "The upload didn't finish. Check your connection and try again.";

/** Build an UploadError from a non-OK response, keeping the server's reason. */
export async function uploadErrorFromResponse(response: Response): Promise<UploadError> {
  let message = "";
  let code: string | undefined;
  try {
    const body = await response.json();
    const detail = body?.detail;
    if (typeof detail === "string") message = detail;
    else if (detail && typeof detail === "object") {
      message = typeof detail.message === "string" ? detail.message : "";
      code = typeof detail.code === "string" ? detail.code : undefined;
    }
  } catch {
    // Non-JSON body (e.g. a proxy's HTML 413 page) — fall through to status copy.
  }
  if (!message) {
    message =
      response.status === 413 ? "This file is too large to upload."
      : response.status === 401 ? "Your session expired. Sign in again and retry."
      : response.status >= 500 ? "Something went wrong on our side. Please try again."
      : "We couldn't accept this file.";
  }
  return new UploadError("rejected", message, response.status, code);
}
